"use client";
import "draft-js/dist/Draft.css";
import React, { useEffect, useRef, useState } from "react";
import { Box, Divider, IconButton, Stack } from "@mui/material";
import {
  Editor as EditorComponent,
  AtomicBlockUtils,
  ContentBlock,
  ContentState,
  convertFromHTML,
  DraftHandleValue,
  EditorState,
  RichUtils,
} from "draft-js";
import { FiBold, FiItalic, FiUnderline } from "react-icons/fi";
import { PiListBullets } from "react-icons/pi";
import { MdFormatListNumbered } from "react-icons/md";
import { stateToHTML } from "draft-js-export-html";
import { BsFileEarmarkImage } from "react-icons/bs";
import { v4 } from "uuid";
import {
  LuAlignCenter,
  LuAlignJustify,
  LuAlignLeft,
  LuAlignRight,
  LuRedo2,
  LuUndo2,
} from "react-icons/lu";
import { uploadFile } from "../../../utils/httpRequests";
import { useAlertContext } from "../../../contexts/alert/AlertProvider";
import { useSelector } from "react-redux";
import { generateLinkImage } from "../../../utils/helpers";
import SelectInput from "../input/SelectInput";

const inlineStyles: { content: React.ReactNode; value: string }[] = [
  { content: <FiBold size={14} />, value: "BOLD" },
  { content: <FiItalic size={14} />, value: "ITALIC" },
  { content: <FiUnderline size={14} />, value: "UNDERLINE" },
];
const blockTypes: { content: React.ReactNode; value: string }[] = [
  { content: <PiListBullets size={14} />, value: "unordered-list-item" },
  { content: <MdFormatListNumbered size={14} />, value: "ordered-list-item" },
];
const blockAligns: { content: React.ReactNode; value: string }[] = [
  { content: <LuAlignLeft size={14} />, value: "left" },
  { content: <LuAlignRight size={14} />, value: "right" },
  { content: <LuAlignCenter size={14} />, value: "center" },
  { content: <LuAlignJustify size={14} />, value: "justify" },
];
const headings: { label: string; value: string }[] = [
  { label: "Normal", value: "unstyled" },
  { label: "H1", value: "header-one" },
  { label: "H2", value: "header-two" },
  { label: "H3", value: "header-three" },
  { label: "H4", value: "header-four" },
  { label: "H5", value: "header-five" },
  { label: "H6", value: "header-six" },
];
const buttonHeight = "35px";

interface IEditorInput {
  value: string;
  onChange: (val: string) => void;
}

function RichTextEditor({ value, onChange }: IEditorInput) {
  const { showAlert } = useAlertContext();
  const { token } = useSelector((state: any) => state.auth);
  const [editorState, setEditorState] = useState<EditorState>(() => {
    if (value) {
      const blocksFromHTML = convertFromHTML(value);
      const contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
      return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
  });
  const editorWrapperRef = useRef<HTMLDivElement>(null);
  const inputImageRef = useRef<HTMLInputElement>(null);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target?.files;
    const file = files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    const resp = await uploadFile({
      formData,
      folder: "forum",
      token: token || "",
    });
    if (!resp || resp?.status !== 200) {
      showAlert({
        type: "error",
        message:
          resp?.data?.error || resp?.data?.message || "Lỗi khi upload hình ảnh",
      });
      return;
    } else {
      addImage(generateLinkImage(resp?.data?.fileUrl));
    }
  };

  const handleEditorChange = (state: EditorState) => {
    setEditorState(state);
  };

  const handleUndo = () => {
    setEditorState(EditorState.undo(editorState));
  };

  const handleRedo = () => {
    setEditorState(EditorState.redo(editorState));
  };

  const handleKeyCommand = (command: string): DraftHandleValue => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return "handled";
    }
    return "not-handled";
  };

  const getCurrentBlockType = () => {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const block = contentState.getBlockForKey(selection.getStartKey());
    return block.getType();
  };

  const checkCurrentInlineStyle = (key: string) => {
    const inlineStyle = editorState.getCurrentInlineStyle();
    return inlineStyle.has(key);
  };

  // Thay đổi kiểu định dạng văn bản (in đậm, nghiêng...)
  const toggleInlineStyle = (style: string) => {
    setEditorState(RichUtils.toggleInlineStyle(editorState, style));
  };

  // Thay đổi kiểu block (danh sách, đoạn văn...)
  const toggleBlockType = (blockType: string) => {
    // restoreSelection(); // Khôi phục vùng chọn trước khi áp dụng kiểu định dạng
    setEditorState(RichUtils.toggleBlockType(editorState, blockType));
    // focusEditor(); // Đảm bảo tiêu điểm quay lại Editor
  };

  const addImage = (imageUrl: string) => {
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      "IMAGE", // Loại thực thể
      "IMMUTABLE", // Không thể chỉnh sửa
      { src: imageUrl } // Dữ liệu metadata cho ảnh
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = AtomicBlockUtils.insertAtomicBlock(
      EditorState.set(editorState, { currentContent: contentStateWithEntity }),
      entityKey,
      " "
    );
    setEditorState(newEditorState);
  };

  useEffect(() => {
    const contentState = editorState.getCurrentContent();
    const html = stateToHTML(contentState); // Chuyển nội dung thành HTML
    onChange(html);
  }, [editorState, onChange]);

  return (
    <Box
      sx={{
        width: "100%",
        border: "2px solid",
        borderColor: "divider",
        borderRadius: "10px",
        p: 1,
      }}
    >
      <Stack>
        {/* Toolbar */}
        <Stack
          direction="row"
          flexWrap="wrap"
          gap={0.5}
          sx={{ pb: 0.5, borderBottom: "1px dashed", borderColor: "divider" }}
        >
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              handleUndo();
            }}
            sx={{
              fontSize: "14px",
              borderRadius: "0px",
              border: "1px solid",
              borderColor: "divider",
              width: "35px",
              height: buttonHeight,
            }}
          >
            <LuUndo2 size={16} />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              handleRedo();
            }}
            sx={{
              fontSize: "14px",
              borderRadius: "0px",
              border: "1px solid",
              borderColor: "divider",
              width: "35px",
              height: buttonHeight,
            }}
          >
            <LuRedo2 size={16} />
          </IconButton>
          <Divider orientation="vertical" flexItem />
          {inlineStyles.map((tool, index) => {
            const active = checkCurrentInlineStyle(tool.value);
            return (
              <IconButton
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  toggleInlineStyle(tool.value);
                }}
                sx={{
                  fontSize: "14px",
                  borderRadius: "0px",
                  border: "1px solid",
                  borderColor: active ? "primary.main" : "divider",
                  width: "35px",
                  height: buttonHeight,
                }}
              >
                {tool.content}
              </IconButton>
            );
          })}
          <Divider orientation="vertical" flexItem />
          <Divider orientation="vertical" flexItem />
          {blockTypes.map((tool, index) => {
            return (
              <IconButton
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  toggleBlockType(tool.value);
                }}
                sx={{
                  fontSize: "14px",
                  borderRadius: "0px",
                  border: "1px solid",
                  borderColor: "divider",
                  width: "35px",
                  height: buttonHeight,
                }}
              >
                {tool.content}
              </IconButton>
            );
          })}
          <Divider orientation="vertical" flexItem />
          <Box sx={{ width: "100px" }}>
            <SelectInput
              direction="column"
              selectSx={{
                height: "34px",
                "& .MuiSelect-select": { px: 1, py: 0.5 },
              }}
              options={headings}
              defaultValue=""
              value={
                getCurrentBlockType().startsWith("align-")
                  ? headings[0].value
                  : getCurrentBlockType() || headings[0].value
              }
              onChange={(val) => toggleBlockType(val)}
            />
          </Box>
          <IconButton
            onClick={() => inputImageRef?.current?.click()}
            sx={{
              fontSize: "14px",
              borderRadius: "0px",
              border: "1px solid",
              borderColor: "divider",
              width: "35px",
              height: buttonHeight,
            }}
          >
            <BsFileEarmarkImage />
            <input
              key={v4()}
              hidden
              ref={inputImageRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />
          </IconButton>
        </Stack>
        {/* Content */}
        <Box
          ref={editorWrapperRef}
          sx={{ border: "1px dashed", borderColor: "divider" }}
        >
          <EditorComponent
            preserveSelectionOnBlur
            editorState={editorState}
            onChange={handleEditorChange}
            blockRendererFn={blockRendererFn}
            handleKeyCommand={handleKeyCommand}
          />
        </Box>
      </Stack>
    </Box>
  );
}

export default RichTextEditor;

const ImageComponent: React.FC<{ contentState: any; block: any }> = ({
  contentState,
  block,
}) => {
  const entity = contentState.getEntity(block.getEntityAt(0));
  const { src } = entity.getData();

  return (
    <img
      src={src}
      alt="Editor content"
      style={{ width: "100%", height: "auto", border: "1px solid" }}
    />
  );
};

const blockRendererFn = (contentBlock: any) => {
  if (contentBlock.getType() === "atomic") {
    return {
      component: ImageComponent,
      editable: false, // Không cho phép chỉnh sửa trực tiếp
    };
  }
  return null;
};
